import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { invalidateAuditCaches } from '@/lib/auditCacheKeys';
import { invalidateCarbonCaches } from '@/lib/carbonCacheKeys';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { IMPORT_MATCH_EMBED, ACCOUNT_TYPE_EMBED } from '@/integrations/supabase/accountingSelects';
// The DETAIL read's select strings live in @releve/shared so the invoice
// document assembler sends the exact same projection (a drifted copy is a
// silently-undefined field on a customer-facing PDF, not a type error).
// The LIST select above stays inline — it is a different projection (nested
// project embed, wider chart_of_accounts columns).
import {
  AR_INVOICE_DOCUMENT_SELECT,
  AR_INVOICE_LINE_ITEMS_SELECT,
} from '@releve/shared/documents/invoice/selects';
import { VAT_DECLARED, type ARInvoiceIndexRowWire } from '@releve/shared';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import {
  api,
  unwrap,
  authedPost,
  authedPatch,
  authedDelete,
  type EnvelopeError,
} from '@/lib/api';
import { addBreadcrumb } from '@/lib/sentry';
import { filterPaymentTransactions, voidedPayments } from '@/lib/invoiceUtils';
import { useToast, toast as standaloneToast } from '@/hooks/useToast';
import { safeFormatDate } from '@/lib/dateUtils';
import i18n from '@/i18n';
import { ARInvoiceStatus } from '@/types/financial-accounts';
import type { TaxTreatment } from '@/lib/taxCalculations';
import { trackEvent } from '@/lib/posthog';
import { toastTaxRateRefusal } from '@/lib/taxRateRefusal';
import { invalidateProjectMoney } from "@/hooks/useProjectMoneySummary";
import {
  AR_LIFECYCLE_ERROR_CODES,
  arLifecycleRefusalText,
  type ARLifecycleRefusalDetails,
} from '@/lib/arInvoiceLifecycle';
import type { CancelARInvoiceResult, ChangeARInvoiceFundResult, IssueARInvoiceResult } from '@releve/shared';

/**
 * Base shape returned by AR mutation RPCs. Only `success` and `error` are
 * universally present. Each RPC may return additional fields — use a narrower
 * type (e.g. `ARInvoiceCreateResult`) at the call site where those fields are
 * guaranteed.
 */
export interface ARInvoiceMutationResult {
  success: boolean;
  error?: string;
  /**
   * The payment ledger transaction id returned by `process_ar_payment`
   * (as `payment_transaction_id`). Used by the bank-feed "Receive a payment"
   * flow to link the created payment txn back to the import. Absent on RPCs
   * that don't create a payment transaction.
   */
  payment_transaction_id?: string;
}

/**
 * Shape returned by `create_ar_invoice_with_line_items` on success. Every
 * manual invoice is created as a DRAFT (post-on-issue, 20261101120000): it
 * books nothing and has no number, so `ledger_transaction_id` and
 * `invoice_number` are null — attachments and carbon key on `ar_invoice_id`.
 */
export interface ARInvoiceCreateResult extends ARInvoiceMutationResult {
  ar_invoice_id: string;
  invoice_number: string | null;
  ledger_transaction_id: string | null;
}

export interface InvoiceLineItem {
  id: string;
  invoice_id: string;
  organization_id: string;
  service_category_id: string | null;
  revenue_account_id: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  tax_rate_id: string | null;
  tax_treatment: TaxTreatment;
  tax_inclusive: boolean;
  tax_amount: number;
  line_total: number;
  line_total_with_tax: number;
  sort_order: number;
  exemption_reason?: string;
  exemption_reason_other?: string;
  /**
   * Rebill link — the expense this line bills on to the customer
   * (`invoice_line_items.source_expense_id`, migration 20260928141345). NULL
   * on an ordinary line, and after the linked expense is deleted (SET NULL).
   * Carried through edits; dropped on clone and recurring templates.
   */
  source_expense_id?: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  service_category?: {
    id: string;
    category_name: string;
    category_code: string;
    category_group: string | null;
  } | null;
}

export interface ARInvoice {
  id: string;
  organization_id: string;
  project_id: string | null;
  business_id: string | null;
  person_id: string | null;
  /** NULL on a draft — Issue assigns it (display via `arInvoiceNumberLabel`). */
  invoice_number: string | null;
  invoice_date: string;
  due_date: string;
  amount_total: number;
  amount_received: number;
  amount_outstanding: number; // Generated column: amount_total - amount_received
  original_currency: string | null; // Currency code for this invoice (e.g., 'USD', 'EUR')
  client_name: string;
  client_email: string | null;
  client_address: string | null;
  description: string;
  payment_terms: string | null;
  status: ARInvoiceStatus;
  metadata: Record<string, unknown> | null;
  self_billing?: boolean;
  additional_vat_notes?: string;
  created_by: string;
  ledger_transaction_id: string | null;
  /**
   * `'channel'` for a POS sale (docs/SALES_CHANNELS.md). Already on the wire —
   * `AR_INVOICE_DOCUMENT_SELECT` is `*` — so the Receivables tab can send a
   * sale to the sales-channels workspace instead of the manual AR form.
   * `'fund'` for a fund installment (`<fund_code>/<n>`, 20261006210000) —
   * opened read-only; `'manual'` otherwise.
   */
  source?: string | null;
  /** The channel a POS sale was rung up on; null for a manual AR invoice. */
  sales_channel_id?: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  line_items?: InvoiceLineItem[];
  person?: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  project?: {
    id: string;
    name: string;
    code: string;
  } | null;
  ledger_transaction?: {
    transaction_number: string;
    project_id?: string;
    fund_id?: string;
    reference?: string | null;
    project?: {
      id: string;
      name: string;
      code: string;
    };
    matched_imports?: Array<{ import: { id: string; booking_date: string; description: string | null } }>;
    created_imports?: Array<{ id: string; booking_date: string; description: string | null }>;
    entries?: Array<{
      id: string;
      account_id: string;
      debit_amount: number;
      credit_amount: number;
      description: string;
      /** Live filed-return tag (`AR_INVOICE_DOCUMENT_SELECT` is `entries(*)`); declared-VAT gate. */
      vat_settlement_id?: string | null;
      chart_of_accounts?: {
        id: string;
        account_name: string;
        currency?: string | null;
        account_type?: { type_name: string | null } | null;
      };
    }>;
  };
  payment_transactions?: Array<{
    id: string;
    transaction_number: string;
    transaction_date: string;
    description: string;
    total_amount: number;
    reference?: string;
    fund_id?: string;
    /** Set when this journal is a VOID reversing another one — see `filterPaymentTransactions`. */
    void_of_transaction_id?: string | null;
    matched_imports?: Array<{ import: { id: string; booking_date: string; description: string | null } }>;
    created_imports?: Array<{ id: string; booking_date: string; description: string | null }>;
    /** A committed bank-feed split that paid this invoice (the third feed link). */
    payment_import_matches?: Array<{ id: string }>;
    entries?: Array<{
      id: string;
      debit_amount: number;
      credit_amount: number;
      description: string;
      chart_of_accounts?: {
        id: string;
        account_name: string;
        currency?: string | null;
        account_type?: { type_name: string | null } | null;
      };
    }>;
  }>;
  /**
   * Undone receipts: each reversed payment paired with the reversal that
   * cancels it, derived by `useARInvoiceById` from the RAW
   * `payment_transactions` embed BEFORE `filterPaymentTransactions` (which
   * strips both halves of every reversal pair — computing this after the
   * narrowing would always yield []). `[]` for channel sales. Drives the
   * invoice's "Voided payments" audit section.
   */
  voided_payments?: Array<{
    payment: NonNullable<ARInvoice['payment_transactions']>[number];
    reversal: NonNullable<ARInvoice['payment_transactions']>[number];
  }>;
}

export interface LineItemInput {
  service_category_id?: string;
  revenue_account_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  tax_rate_id?: string;
  tax_treatment?: TaxTreatment;
  tax_inclusive?: boolean;
  sort_order: number;
  exemption_reason?: string;
  exemption_reason_other?: string;
  /**
   * Rebill link (see `InvoiceLineItem.source_expense_id`). Omitted, never
   * null: the wire schema (`arInvoiceLineItemSchema`) is `.uuid().optional()`.
   * The RPCs coerce an id that is not an expense of the invoice's org to NULL.
   */
  source_expense_id?: string;
}

export interface CreateARInvoiceWithLineItemsData {
  business_id?: string;
  person_id?: string;
  customer_name: string;
  customer_email?: string;
  /**
   * Billing address for the document. Omit to leave a stored address alone
   * (the update RPC's default); `''` clears it, after which the invoice
   * document falls back to the customer record's own address.
   */
  customer_address?: string;
  // No invoice_number: a draft has none — Issue assigns the next one in the
  // org's series (20261101120000).
  invoice_date: string;
  due_date: string;
  /**
   * The supply date (`metadata.supply_date`) — omitted / null = "the same as
   * the invoice date". Editable on a draft; an issued invoice must send the
   * stored one back unchanged (else `ISSUED_LOCKED`).
   */
  supply_date?: string | null;
  description: string;
  reference?: string;
  ar_account_id: string;
  project_id?: string;
  fund_id?: string;
  line_items: LineItemInput[];
}

interface ProcessARPaymentData {
  ar_invoice_id: string;
  payment_amount: number;
  payment_date: string;
  payment_method: string;
  reference?: string;
  bank_account_id: string;
  ar_account_id: string;
  project_id?: string;
  fund_id?: string;
}

/** One row of the slim AR invoice index (the `ar_invoice_index` DB view). */
export type ARInvoiceIndexRow = ARInvoiceIndexRowWire;

/**
 * The COMPLETE slim AR invoice index for an org — the API pages the
 * `ar_invoice_index` view past PostgREST's 1,000-row cap, so it is never
 * silently truncated. Powers the invoices grid's client-side
 * search/sort/filter; heavy detail (line items, journals, payment history) is
 * fetched lazily per row via `useARInvoiceById`.
 *
 * Liveness contract: `entity: 'accountsReceivable'` puts the key under the
 * `['accountsReceivable', orgId]` prefix that EVERY AR mutation in this file
 * already invalidates (create/update/status/payment/delete), as do the
 * cross-domain flows the index's transaction-sourced fields depend on —
 * `ledger_reference`, `fund_id`, `ar_account_id`, `ar_account_currency`,
 * `has_import_link`, `has_unmatched_bank` all come from the posting journal
 * and its import links (useClearingActions, useLedgerTransactions incl.
 * useUpdateLedgerEntryAccount which is reachable from INSIDE the AR expansion
 * panel, useRemoveImportBatch, useApplyTransactionToARInvoice).
 *
 * The domain has THREE names — `ar-invoices` routes, `*_ar_invoice_*` RPCs,
 * `accountsReceivable` query prefix — and the PREFIX is the only load-bearing
 * one: "tidying" it to `arInvoices` would silently kill every invalidation
 * above (no type error, no test failure, just a grid that stops updating).
 * `staleTime` (60s, from indexQueryOptions) only suppresses redundant
 * mount/focus refetches between mutations.
 */
export const useARInvoiceIndex = (
  organizationId?: string,
  options?: { projectId?: string; businessId?: string; personId?: string },
) => {
  // Normalized 3-key shape — a stable key object prevents cache
  // fragmentation between surfaces that omit different options.
  const normalized = {
    projectId: options?.projectId ?? null,
    businessId: options?.businessId ?? null,
    personId: options?.personId ?? null,
  };
  // Key shape, enabled gate, {items} unwrap, staleTime, and the same-org
  // placeholder guard (no cross-org flash) all live in indexQueryOptions.
  return useQuery(
    indexQueryOptions<ARInvoiceIndexRowWire>({
      entity: 'accountsReceivable',
      organizationId,
      path: `/api/v1/ar-invoices/orgs/${organizationId}/ar-invoices/index`,
      params: normalized,
    }),
  );
};

/**
 * INTERIM fat AR invoice list — the pre-index embed shape, still read
 * directly from PostgREST. Its two remaining consumers (ApplyToInvoiceDialog,
 * AccountOverviewTab) want narrower reads — an endpoint-search picker and a
 * reporting RPC respectively; this hook is deleted when they move. Same
 * standing as AP's `useAccountsPayable`.
 *
 * MANUAL invoices only: the query excludes `source='channel'` (see the filter
 * in the queryFn).
 *
 * DO NOT give it a third consumer: the invoices GRID reads
 * `useARInvoiceIndex`, and any other full-org list belongs there too. Its
 * returned API (including `refetch`) is still destructured by callers, so
 * nothing here may be pruned until they are gone.
 */
export const useAccountsReceivable = (organizationId?: string, options?: {
  status?: ARInvoiceStatus;
  customer?: string;
  projectId?: string;
  businessId?: string;
  personId?: string;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['accountsReceivable', organizationId, options],
    queryFn: async () => {
      if (!organizationId) return [];

      let query = supabase
        .from('accounts_receivable')
        .select(`
          *,
          person:people(first_name, last_name, email),
          project:projects(id, name, code),
          ledger_transaction:ledger_transactions!ledger_transaction_id(
            transaction_number,
            project_id,
            fund_id,
            reference,
            project:projects(id, name, code),
            ${IMPORT_MATCH_EMBED},
            entries:ledger_entries(
              id,
              account_id,
              tax_rate_id,
              debit_amount,
              credit_amount,
              description,
              chart_of_accounts(id, account_name, currency, ${ACCOUNT_TYPE_EMBED}),
              tax_rate:tax_rates(name, rate, direction)
            )
          ),
          payment_transactions:ledger_transactions!ar_invoice_id(
            id,
            transaction_number,
            transaction_date,
            description,
            total_amount,
            reference,
            fund_id,
            void_of_transaction_id,
            ${IMPORT_MATCH_EMBED},
            entries:ledger_entries(
              *,
              chart_of_accounts(id, account_name, currency, ${ACCOUNT_TYPE_EMBED}),
              tax_rate:tax_rates(name, rate, direction)
            )
          )
        `)
        .eq('organization_id', organizationId)
        // Channel sales never appear on a manual-AR surface. Both consumers of
        // this hook feed the manual AR write path — ApplyToInvoiceDialog's
        // Apply-to-invoice picker (process_ar_payment) and AccountOverviewTab's
        // invoice modal (update_ar_invoice_with_line_items) — and the
        // CHANNEL_MANAGED fence refuses a `source='channel'` row on every one
        // of them, so listing them here only offers dead-end rows. Same
        // exclusion as the AR index route (apps/api/src/routes/accounting/ar-invoices.ts)
        // and useUnpaidEntitiesForImport.
        // `.filter()` is the typed client's escape hatch for a column the
        // generated types predate. TODO(types-regen): switch to
        // .neq('source', 'channel') once `npm run supabase:types` lands.
        .filter('source', 'neq', 'channel');

      if (options?.status) {
        query = query.eq('status', options.status);
      }

      if (options?.customer) {
        query = query.ilike('client_name', `%${options.customer}%`);
      }

      if (options?.projectId) {
        // Filter AR invoices by project_id directly on the invoice table
        query = query.eq('project_id', options.projectId);
      }

      if (options?.businessId) {
        // Filter AR invoices by their customer business
        query = query.eq('business_id', options.businessId);
      }

      if (options?.personId) {
        query = query.eq('person_id', options.personId);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data: invoices, error } = await query
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!invoices) return [];

      // Fetch line items separately for all invoices
      const invoiceIds = invoices.map(inv => inv.id);
      const { data: lineItems, error: lineItemsError } = await supabase
        .from('invoice_line_items')
        .select(`
          id,
          invoice_id,
          service_category_id,
          revenue_account_id,
          description,
          quantity,
          unit_price,
          tax_rate,
          tax_treatment,
          tax_inclusive,
          exemption_reason,
          exemption_reason_other,
          tax_amount,
          line_total,
          line_total_with_tax,
          sort_order,
          source_expense_id,
          service_category:service_categories(
            id,
            category_name,
            category_code,
            category_group
          )
        `)
        .eq('invoice_type', 'ar')
        .in('invoice_id', invoiceIds);

      if (lineItemsError) throw lineItemsError;

      // Merge line items with invoices. `filterPaymentTransactions` strips
      // the invoice's own creating ledger txn from `payment_transactions`
      // (see lib/invoiceUtils.ts for the rationale).
      const invoicesWithLineItems = invoices.map(invoice => ({
        ...invoice,
        line_items: lineItems?.filter(item => item.invoice_id === invoice.id) || [],
        payment_transactions: filterPaymentTransactions(
          // void_of_transaction_id is load-bearing for the filter — keep it in the
          // cast so dropping it from the select above is a type error, not silence.
          invoice.payment_transactions as Array<{ id: string; void_of_transaction_id?: string | null }> | null,
          invoice.ledger_transaction_id,
        ),
      }));

      return invoicesWithLineItems as ARInvoice[];
    },
    enabled: !!organizationId,
  });
};

export const useARInvoiceById = (organizationId?: string, invoiceId?: string) => {
  return useQuery({
    queryKey: ['arInvoice', organizationId, invoiceId],
    queryFn: async () => {
      if (!organizationId || !invoiceId) return null;

      const { data: invoice, error } = await supabase
        .from('accounts_receivable')
        .select(AR_INVOICE_DOCUMENT_SELECT)
        .eq('organization_id', organizationId)
        .eq('id', invoiceId)
        .single();

      if (error) throw error;
      if (!invoice) return null;

      // Fetch line items separately, in the RPCs' invoice order (sort_order
      // nulls last, then created_at, id) so the Issue dialog's "line N" names
      // the same line `issue_ar_invoice` does.
      const { data: lineItems, error: lineItemsError } = await supabase
        .from('invoice_line_items')
        .select(AR_INVOICE_LINE_ITEMS_SELECT)
        .eq('invoice_type', 'ar')
        .eq('invoice_id', invoiceId)
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });

      if (lineItemsError) throw lineItemsError;

      const rawPayments = invoice.payment_transactions as ARInvoice['payment_transactions'];
      return {
        ...invoice,
        line_items: lineItems || [],
        // Derive the undone-receipt pairs from the RAW array FIRST — the
        // filter below strips both the reversal and the reversed payment.
        // No bank-leg gate for AR ("Deposit to" offers any account); channel
        // sales are excluded outright — their only voided journals are
        // edit_channel_sale's superseded postings, not payments.
        voided_payments:
          invoice.source === 'channel'
            ? []
            : voidedPayments(rawPayments, invoice.ledger_transaction_id, { requireBankLeg: false }),
        payment_transactions: filterPaymentTransactions(
          // void_of_transaction_id is load-bearing for the filter — keep it in the
          // cast so dropping it from the select above is a type error, not silence.
          invoice.payment_transactions as Array<{ id: string; void_of_transaction_id?: string | null }> | null,
          invoice.ledger_transaction_id,
        ),
      } as ARInvoice;
    },
    enabled: !!organizationId && !!invoiceId,
    // AP parity (`useAPInvoiceById`): the multi-embed detail read is heavy and
    // its data only changes through mutations — which all invalidate
    // ['arInvoice', orgId, invoiceId] explicitly. Without this the TanStack
    // defaults refetch on every window focus/remount (once per expanded row),
    // and each refetch mints a new object identity the edit form's hydration
    // effect would see. Deliberately 2 minutes, not the 5 that
    // docs/EXPANDED_ROWS.md §6 suggests — the two AR/AP lazy detail reads stay
    // identical to each other.
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * The exact wire body `POST /api/v1/ar-invoices/orgs/:orgId/ar-invoices`
 * receives (`createARInvoiceSchema` in @releve/shared). ONE function feeds
 * both the create mutation below and the recurring-template payload
 * (`buildRecurringPayload('ar_invoice', body)` strips `invoice_date` +
 * `due_date`), so a key added here reaches the scheduler's replayed payload
 * the moment it lands. Pure — `customer_address` travels as-is (absent vs
 * `''` are different intentions, see the wire schema).
 */
export const buildARInvoiceCreateBody = (data: CreateARInvoiceWithLineItemsData) => ({
  business_id: data.business_id ?? null,
  person_id: data.person_id ?? null,
  customer_name: data.customer_name,
  customer_email: data.customer_email ?? null,
  customer_address: data.customer_address,
  invoice_date: data.invoice_date,
  due_date: data.due_date,
  description: data.description,
  reference: data.reference ?? null,
  ar_account_id: data.ar_account_id,
  project_id: data.project_id ?? null,
  fund_id: data.fund_id ?? null,
  supply_date: data.supply_date ?? null,
  line_items: data.line_items,
});

export type ARInvoiceCreateBody = ReturnType<typeof buildARInvoiceCreateBody>;

/**
 * Every cache a NEW invoice changes. Shared by `useCreateARInvoiceWithLineItems`
 * and the recurring "Run now" mutation (`invalidateGeneratedDocumentCaches`),
 * which creates invoices without going through this hook.
 */
export const invalidateARInvoiceCreateCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
) => {
  // Open History panels / the org Audit Log (docs/AUDIT.md §10a).
  invalidateAuditCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['accountsReceivable', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  // Project spend totals (useProjectMoneySummary) read the same journals.
  invalidateProjectMoney(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
  // Per-account register (bank/deposit overview) — a payment/delete posts to
  // (or removes journals from) accounts, so the register's rows + running
  // balance must refetch.
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
  // Rebill: a line's `source_expense_id` flips the expense's derived
  // "Billed" column (`expense_index.billed_invoice_*`).
  void queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });
};

export const useCreateARInvoiceWithLineItems = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['accountsReceivable', 'create'],
    mutationFn: async ({ organizationId, data }: { organizationId: string; data: CreateARInvoiceWithLineItemsData }) => {
      // SCRUM-184: route through the Hono backend — create_ar_invoice_with_line_items writes the ledger.
      const result = await authedPost<ARInvoiceCreateResult>(
        `/api/v1/ar-invoices/orgs/${organizationId}/ar-invoices`,
        buildARInvoiceCreateBody(data),
      );

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to create AR invoice with line items');
      }

      return result;
    },
    onSuccess: (_, { organizationId, data }) => {
      invalidateARInvoiceCreateCaches(queryClient, organizationId);
      trackEvent('invoice_created', { line_count: data.line_items.length });

      // A DRAFT (20261101120000): no number and nothing booked until Issue.
      standaloneToast.success(i18n.t('invoices.lifecycle.draftSavedTitle'), {
        description: i18n.t('invoices.lifecycle.draftSavedDescription'),
      });
    },
    meta: AR_WRITE_MUTATION_META,
    onError: (error) => {
      // A coded refusal (a foreign tax rate / fund / project / customer).
      if (toastARWriteRefusal(error)) return;
      // A coded tax-rate refusal gets its translated message (the form's catch
      // then skips its generic toast — lib/taxRateRefusal.ts).
      if (toastTaxRateRefusal(error, i18n.t)) return;
      standaloneToast.error(i18n.t('invoices.lifecycle.draftSaveFailedTitle'), {
        description: error.message,
      });
    },
  });
};

/**
 * Process an AR invoice payment.
 *
 * Pattern C: the `process_ar_payment` RPC asserts `verify_backend_token()`
 * (migration 20260519003153). Direct `supabase.rpc(...)` calls fail; we route
 * through the Hono backend's POST /api/v1/accounts-receivable/orgs/:orgId/:invoiceId/payment.
 *
 * Sentry strategy mirrors `useLinkToAccountingImport` /
 * `useClearingActions` — mutationKey + Sentry.withScope tags so the event is
 * searchable in Sentry by `feature: ar-payment`.
 */
export const useProcessARPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['accounts-receivable', 'process-payment'],
    mutationFn: async ({ organizationId, data }: { organizationId: string; data: ProcessARPaymentData }) => {
      addBreadcrumb('payments.ar.process: started', {
        arInvoiceId: data.ar_invoice_id,
        organizationId,
      });
      const res = await api.api.v1['accounts-receivable'].orgs[':orgId'][':invoiceId'].payment.$post({
        param: { orgId: organizationId, invoiceId: data.ar_invoice_id },
        json: {
          payment_amount: data.payment_amount,
          payment_date: data.payment_date,
          payment_method: data.payment_method,
          bank_account_id: data.bank_account_id,
          ar_account_id: data.ar_account_id,
          reference: data.reference ?? null,
          project_id: data.project_id ?? null,
          fund_id: data.fund_id ?? null,
        },
      });
      const result = await unwrap<ARInvoiceMutationResult>(res);
      addBreadcrumb('payments.ar.process: succeeded', {
        arInvoiceId: data.ar_invoice_id,
      });
      return result;
    },
    onSuccess: (_, { organizationId, data }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsReceivable', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
      // Project spend totals (useProjectMoneySummary) read the same journals.
      invalidateProjectMoney(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
      void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
      // Per-account register (bank/deposit overview) — a payment/delete posts to
      // (or removes journals from) accounts, so the register's rows + running
      // balance must refetch.
      void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
      // The lazily-loaded detail (expansion panel, payment form, invoice view)
      // holds the payment history and amount_received this write just changed.
      void queryClient.invalidateQueries({
        queryKey: ['arInvoice', organizationId, data.ar_invoice_id],
      });

      standaloneToast.success(i18n.t('invoices.payment.processedTitle'), {
        description: i18n.t('invoices.payment.processedDescription'),
      });
    },
    onError: (error, { organizationId, data }) => {
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'payments.ar');
        scope.setExtra('organizationId', organizationId);
        scope.setExtra('arInvoiceId', data.ar_invoice_id);
        Sentry.captureException(error);
      });
      standaloneToast.error(i18n.t('invoices.payment.processErrorTitle'), {
        description: error.message,
      });
    },
  });
};

/** Success payload of `POST …/ar-invoices/:invoiceId/undo-payment` (`reverse_ar_payment`). */
export interface UndoARPaymentResult {
  success: boolean;
  reversal_transaction_id: string;
  reversal_transaction_number: string;
  /** `YYYY-MM-DD` — the original payment date, or today if that date is in a filed VAT return. */
  reversal_date: string;
}

/** Refusal codes the API passes through verbatim as the error message. */
const UNDO_AR_PAYMENT_UI_CODES = {
  IMPORT_LINKED: 'importLinked',
  CHANNEL_MANAGED: 'channelManaged',
} as const;

/**
 * Undo (reverse) a received payment on an AR invoice (`reverse_ar_payment`).
 *
 * Posts a reversing journal (the receipt stays; a mirror journal cancels it),
 * dated on the original payment date — or today when that date falls in a
 * filed VAT return. `recompute_ar_invoice_received` then drops the pair, so
 * the invoice returns to sent / partially paid; the corrected payment is
 * recorded afterwards with Receive Payment.
 *
 * Manual receipts only: a bank-feed-linked receipt (`IMPORT_LINKED`) and a
 * channel sale (`CHANNEL_MANAGED`) are refused server-side — workflow rules,
 * not failures, so they get a quiet translated toast and skip Sentry.
 *
 * Pattern C: the RPC asserts `verify_backend_token()`, so it is routed through
 * the Hono backend. AP twin: `useUndoAPPayment`.
 */
export const useUndoARPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['accounts-receivable', 'undo-payment'],
    // Workflow refusals, not failures: App.tsx's MutationCache.onError skips
    // Sentry for these codes; every other error is captured there.
    meta: { expectedErrorCodes: Object.keys(UNDO_AR_PAYMENT_UI_CODES) },
    mutationFn: async ({ organizationId, invoiceId, paymentTransactionId }: {
      organizationId: string;
      invoiceId: string;
      paymentTransactionId: string;
    }) => {
      addBreadcrumb('payments.ar.undo: started', {
        arInvoiceId: invoiceId,
        paymentTransactionId,
        organizationId,
      });
      const result = await authedPost<UndoARPaymentResult>(
        `/api/v1/ar-invoices/orgs/${organizationId}/ar-invoices/${invoiceId}/undo-payment`,
        { payment_transaction_id: paymentTransactionId },
      );
      addBreadcrumb('payments.ar.undo: succeeded', {
        arInvoiceId: invoiceId,
        reversalTransactionId: result.reversal_transaction_id,
      });
      return result;
    },
    onSuccess: (result, { organizationId, invoiceId }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsReceivable', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
      // Project spend totals (useProjectMoneySummary) read the same journals.
      invalidateProjectMoney(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
      void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
      // Per-account register — the reversal posts to the deposit account, so
      // the register's rows + running balance must refetch.
      void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
      // The detail backs the panel's payment history, voided section and the
      // payment form's remaining balance.
      void queryClient.invalidateQueries({ queryKey: ['arInvoice', organizationId, invoiceId] });
      // The reversing deposit entry shifts bank-feed candidates and balances.
      void queryClient.invalidateQueries({ queryKey: ['accountingImports', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['importDetails'] });
      void queryClient.invalidateQueries({ queryKey: ['unpaidEntitiesForImport', organizationId] });

      standaloneToast.success(i18n.t('invoices.undoPayment.successTitle'), {
        description: i18n.t('invoices.undoPayment.successDescription', {
          transactionNumber: result.reversal_transaction_number,
          date: safeFormatDate(result.reversal_date, 'MMM dd, yyyy', result.reversal_date),
        }),
      });
    },
    onError: (error) => {
      // The api answers a UI refusal as `{ error: <code>, code: <code> }`;
      // `error.message` is the fallback for a body without `code`.
      const rawCode = (error as EnvelopeError<{ code?: string }>).envelope?.code ?? error.message;
      const code = UNDO_AR_PAYMENT_UI_CODES[rawCode as keyof typeof UNDO_AR_PAYMENT_UI_CODES];
      if (code) {
        standaloneToast.info(i18n.t(`invoices.undoPayment.${code}Title`), {
          description: i18n.t(`invoices.undoPayment.${code}Description`),
        });
        return;
      }
      // No explicit capture: App.tsx's MutationCache.onError reports it
      // (with the started breadcrumb above on the event).
      standaloneToast.error(i18n.t('invoices.undoPayment.errorTitle'), {
        description: error.message,
      });
    },
  });
};

/**
 * The manual AR write fence (`update_ar_invoice_with_line_items`,
 * `update_ar_invoice_status`, `delete_ar_invoice`, `issue_ar_invoice`,
 * `cancel_ar_invoice`): a channel sale (`CHANNEL_MANAGED`) or a fund
 * installment (`FUND_MANAGED`, 20261006210000) is refused, answered by the
 * api as `{ error: <code>, code: <code> }`. Value = the
 * `invoices.managed.<kind>*` i18n stem. The post-on-issue lifecycle codes
 * (LEGACY_POSTED, HAS_PAYMENTS, …) are worded by `arLifecycleRefusalKey`.
 */
const AR_WRITE_UI_CODES = {
  CHANNEL_MANAGED: 'channel',
  FUND_MANAGED: 'fund',
} as const;

/** Which owner refused a manual AR write, or `null` for any other failure. */
export const arWriteRefusalOf = (error: unknown): 'channel' | 'fund' | null => {
  const envelopeCode = (error as EnvelopeError<{ code?: string }> | null | undefined)?.envelope?.code;
  const code = envelopeCode ?? (error instanceof Error ? error.message : undefined);
  return code && code in AR_WRITE_UI_CODES
    ? AR_WRITE_UI_CODES[code as keyof typeof AR_WRITE_UI_CODES]
    : null;
};

/** The coded refusal a failed AR write carries (`{ code }` body, else the message). */
export const arWriteErrorCode = (error: unknown): string | undefined => {
  const envelopeCode = (error as EnvelopeError<{ code?: string }> | null | undefined)?.envelope?.code;
  return envelopeCode ?? (error instanceof Error ? error.message : undefined);
};

/**
 * A rule refusal of an AR write, in words: a lifecycle code (`HAS_PAYMENTS`,
 * `LEGACY_POSTED`, the date rules — with the date the rule compares against)
 * or a managed-invoice fence. Null for any other failure. The Issue / Cancel
 * dialogs show it inline; the other writes toast it.
 */
export const describeARWriteRefusal = (error: unknown): string | null => {
  const kind = arWriteRefusalOf(error);
  if (kind) return i18n.t(`invoices.managed.${kind}Description`);
  return arLifecycleRefusalText(
    (key, options) => String(i18n.t(key, options)),
    arWriteErrorCode(error),
    (error as EnvelopeError<ARLifecycleRefusalDetails> | null | undefined)?.envelope ?? {},
  );
};

/**
 * A rule refusal as a quiet info toast: the managed-invoice fences ("manage
 * it from its fund / channel") and the lifecycle codes. Returns false when
 * `error` is neither, so the caller falls through to its generic error toast.
 */
const toastARWriteRefusal = (error: unknown): boolean => {
  const kind = arWriteRefusalOf(error);
  if (kind) {
    standaloneToast.info(i18n.t(`invoices.managed.${kind}Title`), {
      description: i18n.t(`invoices.managed.${kind}Description`),
    });
    return true;
  }
  const text = describeARWriteRefusal(error);
  if (!text) return false;
  standaloneToast.info(i18n.t('invoices.lifecycle.refusedTitle'), { description: text });
  return true;
};

/**
 * `meta` for the fenced AR writes: a managed-invoice refusal, a lifecycle
 * refusal and a declared-VAT refusal are user-facing rules, not bugs —
 * App.tsx's MutationCache.onError keeps Sentry quiet for them.
 */
const AR_WRITE_MUTATION_META = {
  expectedErrorCodes: [
    VAT_DECLARED,
    ...Object.keys(AR_WRITE_UI_CODES),
    ...AR_LIFECYCLE_ERROR_CODES,
  ] as readonly string[],
} as const;

/**
 * Every cache a BOOKING change moves — Issue posts the invoice's journal,
 * Cancel posts its reversal, an issued amount edit re-posts it: the AR lists
 * and the detail, the ledger and the per-account registers, the reports that
 * total the ledger (VAT return, P&L, balance sheet, dashboard), project money,
 * carbon (a draft's carbon graduates onto the posting at Issue), the
 * attachment lists and the recurring History (Issue stamps the occurrence's
 * number).
 */
export function invalidateARBookingCaches(
  queryClient: QueryClient,
  organizationId: string,
  invoiceId: string,
) {
  invalidateAuditCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['accountsReceivable', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['arInvoice', organizationId, invoiceId] });
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactionEntries'] });
  invalidateProjectMoney(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances'] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
  // The reports that total the ledger.
  void queryClient.invalidateQueries({ queryKey: ['taxReport', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['incomeStatementData', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['multiProjectIncomeStatement', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['balanceSheetData', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  invalidateCarbonCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['transaction-attachments'] });
  // `RECURRING_TEMPLATES_QUERY_KEY` — spelled out: useRecurringTemplates
  // imports this module (invalidateARInvoiceCreateCaches), so importing it
  // back would be a cycle.
  void queryClient.invalidateQueries({ queryKey: ['recurringTemplates', organizationId] });
  // A cancelled invoice no longer bills its linked expenses ("Billed" column).
  void queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });
}

/**
 * Issue a draft (`POST …/ar-invoices/:id/issue` → `issue_ar_invoice`): the
 * org's next number, booked on `issueDate` (absent = org today), status
 * `sent`. The date rule's refusals (`ISSUE_DATE_FUTURE`,
 * `ISSUE_DATE_BEFORE_LAST`, `VAT_PERIOD_FILED`) come back as coded 400s with
 * the date they need (`last_issued_date` / `today`) on `error.envelope` — the
 * Issue dialog words them inline, so this hook does not toast them.
 */
export const useIssueARInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['accountsReceivable', 'issue'],
    meta: AR_WRITE_MUTATION_META,
    mutationFn: async ({ organizationId, invoiceId, issueDate }: {
      organizationId: string;
      invoiceId: string;
      /** `YYYY-MM-DD`; omitted = the org's today (server-side). */
      issueDate?: string;
    }) => {
      addBreadcrumb('ar.issue: started', { arInvoiceId: invoiceId, organizationId });
      return authedPost<IssueARInvoiceResult>(
        `/api/v1/ar-invoices/orgs/${organizationId}/ar-invoices/${invoiceId}/issue`,
        issueDate ? { issueDate } : {},
      );
    },
    onSuccess: (result, { organizationId, invoiceId }) => {
      invalidateARBookingCaches(queryClient, organizationId, invoiceId);
      standaloneToast.success(i18n.t('invoices.lifecycle.issuedTitle', { number: result.invoice_number }), {
        description: i18n.t('invoices.lifecycle.issuedDescription', {
          number: result.invoice_number,
          date: safeFormatDate(result.invoice_date, 'MMM dd, yyyy', result.invoice_date),
        }),
      });
    },
  });
};

/**
 * Cancel an issued invoice with nothing received (`POST …/:id/cancel` →
 * `cancel_ar_invoice`): a reversal dated `cancelDate` (absent = org today),
 * status `cancelled`. Refusals come back coded (see `useIssueARInvoice`); the
 * Cancel dialog words them inline.
 */
export const useCancelARInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['accountsReceivable', 'cancel'],
    meta: AR_WRITE_MUTATION_META,
    mutationFn: async ({ organizationId, invoiceId, reason, cancelDate }: {
      organizationId: string;
      invoiceId: string;
      reason: string;
      /** `YYYY-MM-DD`; omitted = the org's today (server-side). */
      cancelDate?: string;
    }) => {
      addBreadcrumb('ar.cancel: started', { arInvoiceId: invoiceId, organizationId });
      return authedPost<CancelARInvoiceResult>(
        `/api/v1/ar-invoices/orgs/${organizationId}/ar-invoices/${invoiceId}/cancel`,
        cancelDate ? { reason, cancelDate } : { reason },
      );
    },
    onSuccess: (result, { organizationId, invoiceId }) => {
      invalidateARBookingCaches(queryClient, organizationId, invoiceId);
      standaloneToast.success(i18n.t('invoices.lifecycle.cancelledTitle'), {
        description: i18n.t('invoices.lifecycle.cancelledDescription', {
          transactionNumber: result.void_transaction_number ?? '',
          date: safeFormatDate(result.void_transaction_date, 'MMM dd, yyyy', result.void_transaction_date),
        }),
      });
    },
  });
};

/**
 * Change the fund of an ISSUED, UNPAID invoice (`POST …/:id/fund` →
 * `update_ar_invoice_fund`): re-stamps `fund_id` on the invoice, its posting
 * journal's header and every entry — amounts, dates and VAT never move, so
 * it is offered on a declared invoice and in a filed VAT period too (fund is
 * not a VAT dimension; the `update_expense_header` precedent). `fundId: null`
 * clears the fund. A coded refusal (`HAS_PAYMENTS`, `USE_EDIT`, `CANCELLED`,
 * `FUND_INVALID`, …) is an info toast; anything else an error toast.
 */
export const useChangeARInvoiceFund = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['accountsReceivable', 'changeFund'],
    meta: AR_WRITE_MUTATION_META,
    mutationFn: async ({ organizationId, invoiceId, fundId }: {
      organizationId: string;
      invoiceId: string;
      /** Null = clear the fund. */
      fundId: string | null;
    }) => {
      addBreadcrumb('ar.changeFund: started', { arInvoiceId: invoiceId, organizationId });
      return authedPost<ChangeARInvoiceFundResult>(
        `/api/v1/ar-invoices/orgs/${organizationId}/ar-invoices/${invoiceId}/fund`,
        { fundId },
      );
    },
    onSuccess: (_result, { organizationId, invoiceId }) => {
      invalidateARBookingCaches(queryClient, organizationId, invoiceId);
      // The fund reads (fund detail balances, the funds list) — spelled out:
      // useFunds imports this module, so importing `fundKeys` back would be a cycle.
      void queryClient.invalidateQueries({ queryKey: ['funds', organizationId] });
      standaloneToast.success(i18n.t('invoices.lifecycle.changeFund.changedTitle'), {
        description: i18n.t('invoices.lifecycle.changeFund.changedDescription'),
      });
    },
    onError: (error) => {
      if (toastARWriteRefusal(error)) return;
      standaloneToast.error(i18n.t('invoices.lifecycle.changeFund.failedTitle'), {
        description: error.message,
      });
    },
  });
};

export const useDeleteARInvoice = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    // delete_ar_invoice passes a declared-VAT refusal through as the
    // VAT_DECLARED contract (coded 400). A user-facing rule, not a bug — keep
    // Sentry quiet; the refusal's human text reaches the user via
    // error.message in onError below (same as the update sibling). A
    // channel sale / fund installment is refused too (AR_WRITE_UI_CODES).
    meta: AR_WRITE_MUTATION_META,
    mutationFn: async ({ organizationId, invoiceId }: { organizationId: string; invoiceId: string }) => {
      // SCRUM-184: route through the Hono backend — delete_ar_invoice reverses the ledger.
      const result = await authedDelete<ARInvoiceMutationResult>(
        `/api/v1/ar-invoices/orgs/${organizationId}/ar-invoices/${invoiceId}`,
      );

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to delete AR invoice');
      }

      return result;
    },
    onSuccess: (_, { organizationId, invoiceId }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsReceivable', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
      // Project spend totals (useProjectMoneySummary) read the same journals.
      invalidateProjectMoney(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
      void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
      // Per-account register (bank/deposit overview) — a payment/delete posts to
      // (or removes journals from) accounts, so the register's rows + running
      // balance must refetch.
      void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
      // REMOVE, not invalidate: the id is gone, so an invalidated detail query
      // would refetch a 404 (or sit there stale under a dead id). AP parity.
      queryClient.removeQueries({ queryKey: ['arInvoice', organizationId, invoiceId] });
      // Deleting the invoice's journal cascades its carbon rows out of the
      // Impact slim index (useSustainabilityActivityIndex contract).
      invalidateCarbonCaches(queryClient, organizationId);
      // Deleting the invoice frees any expense it re-billed ("Billed" column).
      void queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });

      toast({
        title: "AR Invoice deleted",
        description: "The accounts receivable invoice has been deleted successfully.",
      });
    },
    onError: (error) => {
      if (toastARWriteRefusal(error)) return;
      toast({
        title: "Error deleting AR invoice",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Cache fan-out after an invoice's lines are rewritten — shared by the invoice
 * update and the grid's line-account edit (useUpdateDocumentLineAccount).
 */
export function invalidateARInvoiceUpdateCaches(
  queryClient: QueryClient,
  organizationId: string,
  invoiceId: string,
) {
  // Open History panels / the org Audit Log (docs/AUDIT.md §10a).
  invalidateAuditCaches(queryClient, organizationId);
  // Invalidate all accountsReceivable queries to ensure the updated data is refetched
  void queryClient.invalidateQueries({ queryKey: ['accountsReceivable'] });
  // Org+id scoped: the detail read this rewrote. (Was a bare ['arInvoice']
  // blanket, which also dropped every OTHER org's cached invoice detail.)
  void queryClient.invalidateQueries({ queryKey: ['arInvoice', organizationId, invoiceId] });
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions'] });
  // Project spend totals (useProjectMoneySummary) read the same journals.
  invalidateProjectMoney(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances'] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance'] });
  // Per-account register (bank/deposit overview) — re-posts the AR journal.
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance'] });
  // Account/amount changes can shift the linked transaction's calculated
  // emissions; refresh dashboards that read from the carbon caches.
  // …and the Impact slim index shows the invoice journal's description/date
  // on its carbon rows (useSustainabilityActivityIndex contract).
  invalidateCarbonCaches(queryClient, organizationId);
  // Adding/removing a rebill line links/frees its expense ("Billed" column).
  void queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });
  // An ISSUED invoice's amount edit re-posts its journal in place, which
  // moves the VAT return, P&L, balance sheet and dashboard totals.
  void queryClient.invalidateQueries({ queryKey: ['taxReport', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['incomeStatementData', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['multiProjectIncomeStatement', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['balanceSheetData', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
}

export const useUpdateARInvoiceWithLineItems = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['accountsReceivable', 'update'],
    // update_ar_invoice_with_line_items deletes and re-posts the ledger lines;
    // when one is already declared on a filed VAT return the
    // guard_declared_vat_entry trigger refuses and the api answers a coded 400
    // (`code: 'VAT_DECLARED'`). A user-facing rule, not a bug — declare it so
    // App.tsx's MutationCache.onError keeps Sentry quiet. The toast below shows
    // the refusal's human text via error.message unchanged. A channel sale /
    // fund installment is refused too (AR_WRITE_UI_CODES).
    meta: AR_WRITE_MUTATION_META,
    mutationFn: async ({ organizationId, invoiceId, data }: { organizationId: string; invoiceId: string; data: CreateARInvoiceWithLineItemsData }) => {
      // SCRUM-184: route through the Hono backend — update_ar_invoice_with_line_items writes the ledger.
      const result = await authedPatch<ARInvoiceMutationResult>(
        `/api/v1/ar-invoices/orgs/${organizationId}/ar-invoices/${invoiceId}`,
        {
          business_id: data.business_id ?? null,
          person_id: data.person_id ?? null,
          customer_name: data.customer_name,
          customer_email: data.customer_email ?? null,
          customer_address: data.customer_address,
          invoice_date: data.invoice_date,
          due_date: data.due_date,
          description: data.description,
          reference: data.reference ?? null,
          ar_account_id: data.ar_account_id,
          project_id: data.project_id ?? null,
          fund_id: data.fund_id ?? null,
          supply_date: data.supply_date ?? null,
          line_items: data.line_items,
        },
      );

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to update AR invoice');
      }

      return result;
    },
    onSuccess: (_, variables) => {
      invalidateARInvoiceUpdateCaches(queryClient, variables.organizationId, variables.invoiceId);

      toast({
        title: "AR Invoice updated",
        description: "The accounts receivable invoice has been updated successfully.",
      });
    },
    onError: (error) => {
      if (toastARWriteRefusal(error)) return;
      if (toastTaxRateRefusal(error, i18n.t)) return;
      toast({
        title: "Error updating AR invoice",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

